import { makeObservable, observable, reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Alert } from 'reactstrap';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import { LoadStatus } from '~/lib/store/LoadingTracker';
import { getAppContext } from '~/lib/AppContext';
import { announce, DialogAnnouncerContext } from './DialogAnnouncer';
import Loader from './Loader';

export interface HasLoadStatus {
    status: LoadStatus;
    lastError?: unknown;
}

export type WaitForProps = {
    operation: HasLoadStatus;
    /**
     * What is rendered while the operation loads:
     *   - `undefined` (omitted): the default `Loader`, with `loadingText`.
     *   - `null`: nothing, and no "loaded" announcement once content appears (for background
     *     loads that should go unnoticed).
     *   - JSX: rendered in place of the default `Loader`.
     */
    loading?: React.ReactNode;
    /**
     * Controls how load errors are surfaced:
     *   - `undefined` (omitted): the global error modal is shown (carrying `errorText` as a
     *     summary above the server's detail). Nothing is rendered inline — except when the
     *     operation arrives already errored, where the modal is suppressed (to avoid
     *     clobbering one the user is reading) and an inline Alert is the sole indicator.
     *   - `null`: silent — neither inline nor modal (for background loads where
     *     failure shouldn't interrupt the user).
     *   - JSX or `(e) => JSX`: rendered inline; the modal is suppressed.
     */
    errored?: React.ReactNode | ((e: unknown) => React.ReactNode);
    loadingText?: string;
    errorText?: string;
    allLoads?: boolean;
    // Message announced to screen readers when the loader is replaced by content.
    // Defaults to a value derived from `loadingText` ("Loading Servers..." ->
    // "Servers loaded"), falling back to "Content loaded".
    loadedAnnouncement?: string;
    children?: () => React.ReactNode;
};

const DEFAULT_ERROR_TEXT = 'There was an error loading this section. Please refresh the page to try again.';

@observer
export default class WaitFor extends React.Component<WaitForProps> {
    static contextType = DialogAnnouncerContext;
    declare context: React.ContextType<typeof DialogAnnouncerContext>;

    unmountHelper = new UnmountHelper(this);

    @observable
    hasLoadedOnce: boolean;

    // Errored without the reaction having announced it (which it only does on an observed
    // status transition), so render() shows an inline Alert as the sole indicator.
    @observable
    unannouncedError: boolean;

    // `props` is not observable, and the reaction below tracks the operation's status from a
    // derivation other than this component's render — reading `this.props` there throws. Mirror
    // the prop instead, re-syncing on update so a swapped operation is still followed.
    //
    // For the reaction only. `render()` deliberately keeps reading `this.props.operation`:
    // own-render prop reads are safe, and the mirror does not advance until
    // componentDidUpdate, so rendering from it would leave render a frame behind a swap.
    // (Contrast `ActionMonitor`, which renders from its mirror because a `@computed` in the
    // same render path has no choice but to read it — there, props would tear against it.)
    @observable.ref
    private trackedOperation: HasLoadStatus;

    constructor(props: WaitForProps) {
        super(props);
        this.hasLoadedOnce = this.props.operation.status === LoadStatus.Complete;
        this.unannouncedError = this.props.operation.status === LoadStatus.Errored;
        this.trackedOperation = this.props.operation;
        makeObservable(this);
    }

    componentDidUpdate(prevProps: WaitForProps) {
        if (prevProps.operation === this.props.operation) return;

        const operation = this.props.operation;
        // A swap re-seeds, but promises nothing about the "loaded" announcement. The reaction
        // decides that from `hasLoadedOnce`, so promising it would mean running the reaction
        // between publishing the swap and re-seeding — an ordering that fails whenever this
        // update is already inside a MobX batch, which it is whenever a parent swaps from an
        // action. Nothing swaps today (every `operation=` in `src/` is a stable field path),
        // so a missed announcement does not justify that timing dependency. The re-seeds below
        // stay because getting those wrong is visible rather than merely inaudible.
        runInAction(() => {
            this.trackedOperation = operation;
            // The replacement has its own load history, so a completed predecessor must not
            // suppress its loader.
            this.hasLoadedOnce = operation.status === LoadStatus.Complete;
            // The reaction only fires when the status *value* changes, so a replacement that
            // arrives already errored alongside an errored predecessor is never announced.
            // Flag it so render() surfaces the inline alert instead of leaving it blank.
            this.unannouncedError =
                operation.status === LoadStatus.Errored && prevProps.operation.status === LoadStatus.Errored;
        });
    }

    componentDidMount() {
        // Only fire the modal on transitions into Errored *during this component's
        // lifetime* — not for a store that was already errored at mount. Mount-time-errored
        // stores still surface inline via render(), so users see them, but a newly-mounted
        // WaitFor can no longer clobber an unrelated error modal the user is currently reading.
        const disposeReaction = reaction(
            () => this.trackedOperation.status,
            (newStatus, prevStatus) => {
                // Capture whether the loader was on screen before this transition,
                // while `hasLoadedOnce` still holds its pre-transition value.
                const loaderWasShown = this.isLoaderShown(prevStatus);

                if (newStatus === LoadStatus.Complete && !this.hasLoadedOnce) {
                    runInAction(() => {
                        this.hasLoadedOnce = true;
                    });
                }

                // A fresh load attempt means any resulting error is an in-life transition the
                // reaction will announce, so it no longer needs the inline fallback.
                if (newStatus !== LoadStatus.Errored && this.unannouncedError) {
                    runInAction(() => {
                        this.unannouncedError = false;
                    });
                }

                if (newStatus === LoadStatus.Errored) {
                    if (this.hasLoadedOnce) {
                        runInAction(() => {
                            this.hasLoadedOnce = false;
                        });
                    }
                    // Modal behavior by `errored` prop value:
                    //   undefined  → show the modal (caller hasn't handled the error)
                    //   null       → silent (caller explicitly opts out of both inline and modal)
                    //   JSX / fn   → skip the modal (caller renders their own inline error)
                    if (this.props.errored === undefined) {
                        // The modal renders the structured server error; errorText rides along as a
                        // human summary shown above the server's detail. Keyed on the Errored status
                        // rather than lastError's truthiness, so an error with a missing/falsy value
                        // still surfaces the modal instead of leaving the section silently blank.
                        getAppContext().error(
                            this.props.operation.lastError,
                            this.props.errorText ? { errorSummary: this.props.errorText } : undefined
                        );
                    }
                }

                // Announce only when the loader was actually replaced by content.
                // This covers first loads and `allLoads` reloads, but not silent
                // background reloads (where stale content stayed on screen), nor a
                // `loading={null}` caller, which showed nothing to replace.
                if (newStatus === LoadStatus.Complete && loaderWasShown && this.props.loading !== null) {
                    announce(this.context, this.loadedAnnouncementText());
                }
            }
        );
        this.unmountHelper.onUnmount(disposeReaction);
    }

    // The loader state for a settled operation: shown while pending, and while loading
    // unless stale content is being kept on screen. render() has one further trigger — a
    // swap the mirror has not caught up to — that cannot apply here, because the only
    // caller is the status reaction and componentDidUpdate publishes the swap before it.
    private isLoaderShown(status: LoadStatus): boolean {
        if (status === LoadStatus.Pending) {
            return true;
        }
        return status === LoadStatus.Loading && ((this.props.allLoads ?? false) || !this.hasLoadedOnce);
    }

    private loadedAnnouncementText(): string {
        if (this.props.loadedAnnouncement) {
            return this.props.loadedAnnouncement;
        }
        const subject = this.props.loadingText
            ?.replace(/^loading\b\s*/i, '')
            .replace(/[.…]+$/, '')
            .trim();
        return subject ? `${subject} loaded` : 'Content loaded';
    }

    render() {
        const status = this.props.operation.status;
        const { allLoads = false, children, errored } = this.props;
        const fallback =
            this.props.loading === undefined ? (
                <Loader text={this.props.loadingText ?? 'Loading...'} />
            ) : (
                this.props.loading
            );

        if (status === LoadStatus.Pending) {
            return fallback;
        }

        // `hasLoadedOnce` describes the *previous* operation until componentDidUpdate re-seeds
        // it, which runs after this render. Treat a not-yet-mirrored swap as unloaded so the
        // children closure is never invoked for an operation that has never completed.
        const awaitingSwapSync = this.props.operation !== this.trackedOperation;

        if (status === LoadStatus.Loading && (allLoads || !this.hasLoadedOnce || awaitingSwapSync)) {
            return fallback;
        }

        if (status === LoadStatus.Errored) {
            if (typeof errored === 'function') {
                return (errored as (e: unknown) => React.ReactNode)(this.props.operation.lastError);
            }
            if (errored !== undefined) {
                // Explicit JSX (or null for silent) — caller controls the inline render.
                return errored;
            }
            // errored omitted: the modal is the error surface, so render nothing inline — except
            // for an unannounced error (no modal fired), where this inline Alert is the only
            // indicator so the section isn't left silently blank.
            return this.unannouncedError ? (
                <Alert color="danger">
                    <i className="ion-md-warning me-2" aria-hidden="true" />
                    {this.props.errorText ?? DEFAULT_ERROR_TEXT}
                </Alert>
            ) : null;
        }

        return children?.();
    }
}
